import { Link } from 'react-router-dom'
import { ArrowUpRight, Copy } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useAuth,
  useLatestLotteryResult,
  useLotteryResults,
  useUserGeneratedNumbers,
} from '@workspace/public-utils'
import { Seo } from '@workspace/public-ui/components/Seo'
import BrandMark from '../components/BrandMark'
import NumberBalls from '../components/NumberBalls'
import { useBannerAnnouncements } from '../hooks/useBannerAnnouncements'

const dateLabel = (date: string) =>
  new Date(date).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Seoul',
  })

function TicketStatus({
  loading,
  error,
  retry,
}: {
  loading: boolean
  error: boolean
  retry: () => void
}) {
  return (
    <div className="md-empty" role="status">
      <p>
        {loading
          ? '번호표를 준비하고 있어요.'
          : error
            ? '번호를 불러오지 못했어요.'
            : '아직 준비된 번호가 없어요.'}
      </p>
      {error && (
        <button className="md-button md-button-outline" type="button" onClick={retry}>
          다시 불러오기
        </button>
      )}
    </div>
  )
}

function TicketRow({ numbers, label, meta }: { numbers: number[]; label: string; meta: string }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(numbers.join(', '))
      toast.success('번호를 복사했어요.')
    } catch {
      toast.error('복사하지 못했어요. 번호를 직접 선택해 주세요.')
    }
  }
  return (
    <div className="md-ticket-row">
      <div className="md-set-label">
        <strong>{label}</strong>
        <small>{meta}</small>
      </div>
      <NumberBalls numbers={numbers} />
      <button
        className="md-copy-button"
        onClick={copy}
        type="button"
        aria-label={`${label} ${meta} 번호 복사`}
      >
        <Copy size={14} aria-hidden="true" />
        <span>복사</span>
      </button>
    </div>
  )
}

function MemberNumbers() {
  const { data, isLoading, isError, refetch } = useUserGeneratedNumbers(0, 5)
  const rows = data?.generated_numbers ?? []
  if (!rows.length)
    return (
      <TicketStatus
        loading={isLoading}
        error={isError}
        retry={() => {
          void refetch()
        }}
      />
    )
  return (
    <>
      {rows.map((row, index) => (
        <TicketRow
          key={row.id}
          numbers={row.numbers}
          label={String.fromCharCode(65 + index)}
          meta={row.round_target ? `${row.round_target}회` : '내 번호'}
        />
      ))}
    </>
  )
}

function RecentNumbers() {
  const { data, isLoading, isError, refetch } = useLotteryResults(0, 5)
  const rows = data?.items ?? []
  if (!rows.length)
    return (
      <TicketStatus
        loading={isLoading}
        error={isError}
        retry={() => {
          void refetch()
        }}
      />
    )
  return (
    <>
      {rows.map((row) => (
        <TicketRow
          key={row.round_number}
          numbers={row.numbers}
          label={String(row.round_number)}
          meta="회차"
        />
      ))}
    </>
  )
}

const steps = [
  ['번호를 확인하고', '나에게 도착한 여섯 숫자'],
  ['기록을 살펴보고', '회차별 결과와 번호 분석'],
  ['한 주를 기다려요', '내 번호함에서 다시 만나요'],
]
const process = [
  [
    '나의 번호를 한자리에',
    '로그인하면 나에게 배정된 번호를 확인할 수 있어요. 회차별로 정리된 번호표를 꺼내보세요.',
  ],
  [
    '여섯 숫자를 한 번 더 확인',
    '최근 추첨 결과와 보너스 번호를 살펴보세요. 지난 회차의 기록도 함께 확인할 수 있어요.',
  ],
  [
    '결과 곁에 남기는 조합 기록',
    '번호의 합계와 홀짝 구성, 구간별 분포를 살펴보세요. 숫자에 담긴 특징을 차분히 읽어보세요.',
  ],
]

export default function LottomdHomePage() {
  const { isAuthenticated } = useAuth()
  const { data: latest, isLoading, isError, refetch } = useLatestLotteryResult()
  const { data: announcements = [] } = useBannerAnnouncements()
  const numbers = latest?.numbers ?? []
  const sum = numbers.reduce((total, number) => total + number, 0)
  const odd = numbers.filter((number) => number % 2 !== 0).length
  const bands = [
    [1, 10],
    [11, 20],
    [21, 30],
    [31, 40],
    [41, 45],
  ]
  return (
    <div className="md-home">
      <Seo
        title="로또명당 · 한 주의 기대를 담는 곳"
        description="나의 로또 번호와 최신 추첨 결과를 한 장의 번호표로. 로또명당에서 번호 기록과 분석을 확인하세요."
        canonicalPath="/"
      />
      <section className="md-hero" aria-labelledby="hero-title">
        <div className="md-container md-hero-grid">
          <div className="md-hero-copy">
            <div className="md-eyebrow">
              <span>6/45</span>나만의 로또 명당
            </div>
            <h1 id="hero-title">
              이번 주 번호도,
              <br />
              <em>
                <img src="/brand/myungdang.svg" alt="명당" width="246" height="142" />
              </em>
              에서 받아가세요.
            </h1>
            <p className="md-hero-lede">
              번호를 고르는 작은 설렘, 기다리는 한 주의 즐거움.
              <br />
              나의 숫자와 지난 기록을
              <br className="md-desktop-break" /> 한 장의 번호표에 담아드립니다.
            </p>
            <div className="md-hero-actions">
              <Link
                to={isAuthenticated ? '/dashboard/numbers' : '/auth/login'}
                className="md-button md-button-outline"
              >
                내 번호 확인하기
                <ArrowUpRight size={18} aria-hidden="true" />
              </Link>
              <a className="md-text-link" href="#process">
                어떻게 이용하나요?
              </a>
            </div>
            <p className="md-hero-note">나의 번호표 · 최신 추첨 결과 · 번호별 기록</p>
          </div>
          <div className="md-slip-wrap">
            <article className="md-slip" aria-label="최근 추첨 번호표" aria-busy={isLoading}>
              <header className="md-slip-top">
                <div>
                  <BrandMark />
                  <p>당신의 한 주에, 좋은 기대 한 장</p>
                </div>
                <div className="md-slip-edition">
                  6/45<small>로또 번호표</small>
                </div>
              </header>
              <div className="md-slip-body">
                <div className="md-slip-caption">
                  <strong>{latest ? `제 ${latest.round_number}회` : '최근 추첨 결과'}</strong>
                  <span>
                    {latest
                      ? dateLabel(latest.draw_date)
                      : isLoading
                        ? '불러오는 중'
                        : '결과 대기 중'}
                  </span>
                </div>
                <NumberBalls numbers={numbers} />
                <dl className="md-slip-facts">
                  <div>
                    <dt>보너스 번호</dt>
                    <dd>
                      <strong>{latest?.bonus_number ?? '—'}</strong>
                      <span>여섯 숫자에 더하는 하나</span>
                    </dd>
                  </div>
                  <div>
                    <dt>번호 합계</dt>
                    <dd>
                      <strong>{numbers.length ? sum : '—'}</strong>
                      <span>오름차순으로 읽는 기록</span>
                    </dd>
                  </div>
                </dl>
                {isError && (
                  <button
                    className="md-text-link"
                    type="button"
                    onClick={() => {
                      void refetch()
                    }}
                  >
                    추첨 결과 다시 불러오기
                  </button>
                )}
              </div>
              <footer className="md-slip-stub">
                <p>
                  <strong>잘 챙겨두세요.</strong>
                  <br />
                  기다리는 마음까지 담았으니까요.
                </p>
                <img src="/brand/expectation-stamp.svg" alt="좋은 기대" width="84" height="84" />
              </footer>
            </article>
            <p className="md-slip-footnote">
              좋은 기대 한 장{' '}
              <span>
                {latest ? `${latest.round_number}회 추첨 결과` : '추첨 결과를 기다리고 있어요'}
              </span>
            </p>
            {latest && (
              <Link to={`/results/${latest.round_number}`} className="md-slip-detail">
                이 회차 자세히 보기 <ArrowUpRight size={13} aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </section>
      <section className="md-visit" aria-label="로또명당 이용 순서">
        <div className="md-container">
          <ol>
            {steps.map(([title, detail], i) => (
              <li key={title}>
                <span>0{i + 1}</span>
                <div>
                  <strong>{title}</strong>
                  <small>{detail}</small>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
      {announcements.length > 0 && (
        <aside className="md-container md-announcements" aria-label="공지사항">
          {announcements.map((item) => (
            <p key={item.id}>
              <span>공지</span>
              {item.title}
            </p>
          ))}
        </aside>
      )}
      <section className="md-section md-process" id="process" aria-labelledby="process-title">
        <div className="md-container">
          <div className="md-section-heading md-center">
            <span className="md-kicker">명당이 번호를 살피는 기준</span>
            <h2 id="process-title">
              기대는 가볍게,
              <br />
              확인은 꼼꼼하게.
            </h2>
            <p>
              좋은 기대를 품고 다시 찾는 자리.
              <br />
              번호를 확인하는 과정도 차분하고 분명했으면 합니다.
            </p>
          </div>
          <ol className="md-process-list">
            {process.map(([title, detail], i) => (
              <li key={title}>
                <span className="md-process-number">0{i + 1}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section
        className="md-section md-numbers-section"
        id="numbers"
        aria-labelledby="numbers-title"
      >
        <div className="md-container">
          <div className="md-section-heading md-center">
            <span className="md-kicker">번호를 만나는 곳</span>
            <h2 id="numbers-title">한 장에 담긴, 여섯 개의 기대.</h2>
            <p>
              나의 번호와 지난 추첨 기록을
              <br />
              언제든 편하게 꺼내보세요.
            </p>
          </div>
          <div className="md-ticket-layout">
            <aside className="md-ticket-guide">
              <span className="md-kicker">LOTTO MYEONGDANG</span>
              <h3>나의 번호를 찾는 자리</h3>
              <p>
                {isAuthenticated
                  ? '최근 배정된 번호를 보여드려요. 내 번호함에서 전체 기록을 확인하세요.'
                  : '로그인하면 나에게 배정된 번호를 확인할 수 있어요.'}
              </p>
              <Link
                to={isAuthenticated ? '/dashboard/numbers' : '/auth/login'}
                className="md-button"
              >
                {isAuthenticated ? '내 번호함 열기' : '로그인하고 번호 보기'}
                <ArrowUpRight size={16} aria-hidden="true" />
              </Link>
              <div className="md-guide-links">
                <Link to="/plans">
                  나에게 맞는 구독 안내<span aria-hidden="true">↗</span>
                </Link>
                <Link to="/analysis">
                  번호별 기록과 분석<span aria-hidden="true">↗</span>
                </Link>
                <Link to="/results">
                  지난 회차 당첨결과<span aria-hidden="true">↗</span>
                </Link>
              </div>
            </aside>
            <article className="md-ticket">
              <header className="md-ticket-head">
                <div>
                  <h3>
                    <BrandMark />
                    <span>번호표</span>
                  </h3>
                  <p>
                    {isAuthenticated
                      ? '나에게 배정된 최근 번호'
                      : '최근 5회 추첨 결과 · 보너스 번호 제외'}
                  </p>
                </div>
                <span className="md-badge">{isAuthenticated ? '내 번호' : '추첨 기록'}</span>
              </header>
              <div className="md-ticket-list">
                {isAuthenticated ? <MemberNumbers /> : <RecentNumbers />}
              </div>
              <footer className="md-ticket-foot">
                <strong>잘 챙겨두세요. 기다리는 마음까지 담았으니까요.</strong>
                <span>
                  {isAuthenticated
                    ? '전체 번호와 회차별 기록은 내 번호함에서 확인하세요.'
                    : '회차별 보너스 번호와 당첨 정보는 당첨결과에서 확인하세요.'}
                </span>
              </footer>
            </article>
          </div>
        </div>
      </section>
      <section className="md-section md-statistics" aria-labelledby="statistics-title">
        <div className="md-container md-content-width">
          <div className="md-section-heading md-heading-row">
            <div>
              <span className="md-kicker">최근 추첨 번호의 조합 기록</span>
              <h2 id="statistics-title">숫자, 한 번 더 살펴보세요.</h2>
            </div>
            <p>
              합계와 홀짝 구성으로 읽는 여섯 숫자.
              <br />
              {latest
                ? `${latest.round_number}회 추첨 번호 기준입니다.`
                : '추첨 결과가 준비되면 표시됩니다.'}
            </p>
          </div>
          <dl className="md-overview">
            <div>
              <dt>확인한 회차</dt>
              <dd>{latest ? `${latest.round_number}회` : '—'}</dd>
            </div>
            <div>
              <dt>홀수 · 짝수 개수</dt>
              <dd>{numbers.length ? `${odd} / ${numbers.length - odd}` : '—'}</dd>
            </div>
            <div>
              <dt>번호 합계</dt>
              <dd>{numbers.length ? sum : '—'}</dd>
            </div>
          </dl>
          <details className="md-stat-details">
            <summary>번호 구간별 분포 자세히 보기</summary>
            <div className="md-range-list">
              {bands.map(([min, max], index) => {
                const count = numbers.filter((number) => number >= min && number <= max).length
                return (
                  <div className="md-range-row" key={min}>
                    <span>
                      {min}–{max}
                    </span>
                    <div className="md-range-track">
                      <div
                        className={`md-range-fill md-ball-${index + 1}`}
                        style={{ width: `${numbers.length ? (count / numbers.length) * 100 : 0}%` }}
                      />
                    </div>
                    <strong>{numbers.length ? `${count}개` : '—'}</strong>
                  </div>
                )
              })}
              <Link to="/analysis" className="md-text-link">
                더 많은 번호 분석 보기 <ArrowUpRight size={15} aria-hidden="true" />
              </Link>
            </div>
          </details>
        </div>
      </section>
      <section className="md-section md-faq-section" aria-labelledby="faq-title">
        <div className="md-container md-content-width md-faq-grid">
          <div className="md-section-heading">
            <span className="md-kicker">편하게 이용하시도록</span>
            <h2 id="faq-title">궁금하실 이야기</h2>
          </div>
          <div>
            <details className="md-faq">
              <summary>나의 번호는 어디에서 확인하나요?</summary>
              <p>
                로그인 후 상단의 내 번호함에서 확인할 수 있어요. 배정된 번호와 회차별 기록을 함께
                살펴보세요.
              </p>
            </details>
            <details className="md-faq">
              <summary>구독 서비스는 어떻게 이용하나요?</summary>
              <p>
                구독플랜에서 제공 내용을 살펴보고 상담을 신청해 주세요. 가입과 이용 방법을 안내해
                드립니다. <Link to="/plans">구독 안내 보기</Link>
              </p>
            </details>
            <details className="md-faq">
              <summary>지난 회차의 결과도 볼 수 있나요?</summary>
              <p>
                당첨결과에서 회차별 번호와 보너스 번호, 당첨 정보를 확인할 수 있어요.{' '}
                <Link to="/results">당첨결과 보기</Link>
              </p>
            </details>
            <Link to="/faq" className="md-text-link md-more-help">
              이용 안내 더 보기 <ArrowUpRight size={15} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
